import React, { useEffect, useRef, useState } from "react";
import { api, resolveImageUrl, AVAILABILITY } from "@/lib/api";
import { MATCH, IG_LOOKUP } from "@/constants/testIds";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Link2, Sparkles, CheckCircle2, Instagram } from "lucide-react";

function ResultCard({ p, exact = false }) {
  const pct = Math.round(p.similarity || 0);
  return (
    <div
      data-testid={MATCH.resultCard(p.id)}
      className={`paper-card overflow-hidden group hover:-translate-y-1 transition-all duration-300 animate-fade-up relative ${
        exact ? "ring-2 ring-terracotta shadow-md" : ""
      }`}
    >
      <div className="aspect-[3/4] bg-stone-100 overflow-hidden relative">
        <img
          src={resolveImageUrl(p.photo_url)}
          alt={p.name}
          className="w-full h-full object-cover"
        />
        <div
          className={`absolute top-2 right-2 rounded-full px-2.5 py-1 text-xs font-semibold ${
            pct >= 80
              ? "bg-terracotta text-white"
              : pct >= 60
                ? "bg-saffron text-white"
                : "bg-white/90 text-stone-700 border border-stone-200"
          }`}
        >
          {pct}% match
        </div>
        {exact && (
          <div className="absolute top-2 left-2 bg-white/95 text-terracotta text-[10px] uppercase tracking-widest font-bold border border-terracotta/30 rounded-full px-2 py-0.5">
            Exact match
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-serif text-base text-stone-900 truncate">
              {p.name}
            </div>
            <div className="text-[11px] text-stone-500">{p.fabric}</div>
          </div>
          <div className="text-terracotta text-sm font-medium">
            ₹{Number(p.price).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="mt-1 text-[11px] text-stone-500">{p.availability}</div>
      </div>
    </div>
  );
}

export default function Match() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [budget, setBudget] = useState("");
  const [availability, setAvailability] = useState("any");
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [igUrl, setIgUrl] = useState("");
  const [igImages, setIgImages] = useState([]);
  const [igBusy, setIgBusy] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/match/history");
      setHistory(data || []);
    } catch (err) {
      console.warn('Saheli: request failed', err);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const runMatchFile = async (file) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (budget) fd.append("max_budget", budget);
      if (availability !== "any") fd.append("availability", availability);
      const { data } = await api.post("/match", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      loadHistory();
      toast.success("Saheli found matches");
    } catch (e) {
      toast.error("Match failed — try another image");
    } finally {
      setBusy(false);
    }
  };

  const runMatchUrl = async () => {
    if (!urlInput.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/match", {
        image_url: urlInput.trim(),
        max_budget: budget ? Number(budget) : undefined,
        availability_filter: availability === "any" ? undefined : availability,
      });
      setResult(data);
      loadHistory();
      toast.success("Saheli found matches");
    } catch {
      toast.error("Could not fetch image from that URL");
    } finally {
      setBusy(false);
    }
  };

  const igLookup = async () => {
    if (!igUrl.trim()) return;
    setIgBusy(true);
    setIgImages([]);
    try {
      const { data } = await api.post("/instagram/extract", {
        url: igUrl.trim(),
      });
      const imgs = data.images || [];
      setIgImages(imgs);
      if (imgs.length === 0) toast.warning("No images found on that page");
      else
        toast.success(
          `Found ${imgs.length} image${imgs.length === 1 ? "" : "s"}`,
        );
    } catch (e) {
      const msg = e?.response?.data?.detail || "Extraction failed";
      toast.error(String(msg));
    } finally {
      setIgBusy(false);
    }
  };

  const matchIgThumb = async (url) => {
    setUrlInput(url);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/match", {
        image_url: url,
        max_budget: budget ? Number(budget) : undefined,
        availability_filter: availability === "any" ? undefined : availability,
      });
      setResult(data);
      loadHistory();
      toast.success("Saheli found matches");
      setTimeout(() => {
        document
          .querySelector('[data-testid="match-results"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch {
      toast.error("Match failed on that image");
    } finally {
      setBusy(false);
    }
  };

  const markSent = async (id) => {
    try {
      await api.patch(`/match/history/${id}`, { sent_to_customer: true });
      toast.success("Marked sent");
      loadHistory();
    } catch {
      toast.error("Failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) runMatchFile(file);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="overline">Visual search</div>
        <h1 className="font-serif text-4xl text-stone-900 mt-1">Saheli Match</h1>
        <p className="text-stone-500 text-sm mt-2">
          Drop a saree photo (or paste any Instagram / product URL) — Saheli will
          find the closest pieces in your loom.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid={MATCH.uploadZone}
          onClick={() => fileRef.current?.click()}
          className={`paper-card p-6 border-dashed border-2 cursor-pointer hover:border-terracotta/40 transition-colors ${
            dragOver ? "border-terracotta bg-terracotta/5" : "border-stone-300"
          }`}
        >
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) runMatchFile(f);
            }}
          />
          <div className="flex flex-col items-center justify-center text-center gap-2 py-6">
            <div className="w-12 h-12 rounded-full bg-terracotta/10 text-terracotta grid place-items-center">
              <Upload className="w-5 h-5" />
            </div>
            <div className="font-serif text-xl text-stone-800">
              Drop an image here
            </div>
            <div className="text-xs text-stone-500 max-w-xs">
              JPG, PNG or WebP. Or click to choose from your device.
            </div>
          </div>
        </div>

        <div className="paper-card p-6 lg:col-span-2 space-y-4">
          <div>
            <label className="overline">Or paste a link</label>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 relative">
                <Link2 className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid={MATCH.urlInput}
                  className="pl-9"
                  placeholder="Instagram post URL or any product page"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                />
              </div>
              <Button
                data-testid={MATCH.urlSubmit}
                onClick={runMatchUrl}
                disabled={busy}
                className="bg-terracotta hover:bg-terracotta-dark rounded-full"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                {busy ? "Matching…" : "Find match"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline">Max budget (₹)</label>
              <Input
                data-testid={MATCH.budgetInput}
                type="number"
                placeholder="e.g. 15000"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <label className="overline">Availability</label>
              <Select value={availability} onValueChange={setAvailability}>
                <SelectTrigger data-testid={MATCH.availabilityFilter} className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {AVAILABILITY.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid={IG_LOOKUP.section}
        className="paper-card p-6 space-y-3"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-fuchsia-50 text-fuchsia-700 grid place-items-center">
            <Instagram className="w-4 h-4" />
          </div>
          <div>
            <div className="overline">Instagram / product page lookup</div>
            <h3 className="font-serif text-xl text-stone-800">
              Pull candidate images from a link
            </h3>
          </div>
        </div>
        <p className="text-xs text-stone-500 max-w-2xl">
          Paste an Instagram post or reel URL, a Pinterest pin, or any product
          page. Saheli pulls the images and lets you match any one of them to
          your catalog in a single click. Note: Instagram profile URLs are
          heavily protected — for best results, paste individual post URLs.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Link2 className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid={IG_LOOKUP.urlInput}
              className="pl-9"
              placeholder="https://www.instagram.com/p/… or any product page"
              value={igUrl}
              onChange={(e) => setIgUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && igLookup()}
            />
          </div>
          <Button
            data-testid={IG_LOOKUP.submit}
            onClick={igLookup}
            disabled={igBusy}
            variant="outline"
            className="rounded-full border-terracotta/30 text-terracotta hover:bg-terracotta/5"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {igBusy ? "Fetching…" : "Fetch images"}
          </Button>
        </div>
        {igImages.length > 0 && (
          <div
            data-testid={IG_LOOKUP.results}
            className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-2"
          >
            {igImages.map((u, i) => (
              <button
                key={u + i}
                data-testid={IG_LOOKUP.thumb(i)}
                onClick={() => matchIgThumb(u)}
                className="aspect-square rounded-lg overflow-hidden border border-stone-200 hover:border-terracotta hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 relative group"
              >
                <img src={u} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-terracotta/0 group-hover:bg-terracotta/20 transition-colors" />
                <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[10px] bg-white/95 text-terracotta rounded-full px-2 py-0.5 font-medium">
                    Match this
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {busy && (
        <div className="paper-card p-8 text-center">
          <div className="inline-block w-6 h-6 border-2 border-terracotta/30 border-t-terracotta rounded-full animate-spin"></div>
          <div className="mt-3 text-sm text-stone-500">
            Saheli is examining fabric, motifs and colors…
          </div>
        </div>
      )}

      {result && (
        <div data-testid={MATCH.results} className="space-y-6">
          <div className="paper-card p-5">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {result.query_image_url && (
                <img
                  src={resolveImageUrl(result.query_image_url)}
                  alt=""
                  className="w-32 h-32 rounded-xl object-cover border border-stone-200"
                />
              )}
              <div className="flex-1">
                <div className="overline">Saheli sees</div>
                <div className="font-serif text-xl text-stone-800 mt-1">
                  {result.query_features?.style || "A beautiful saree"}
                </div>
                <div className="text-sm text-stone-600 mt-1">
                  {result.query_features?.description}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {result.query_features?.fabric && (
                    <span className="heritage-badge">
                      Fabric · {result.query_features.fabric}
                    </span>
                  )}
                  {(result.query_features?.primary_colors || []).map((c) => (
                    <span key={c} className="heritage-badge">
                      {c}
                    </span>
                  ))}
                  {(result.query_features?.motifs || []).map((m) => (
                    <span
                      key={m}
                      className="heritage-badge bg-terracotta/5 text-terracotta border-terracotta/20"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {result.exact_match && (
            <div>
              <div className="overline mb-2">Exact match</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <ResultCard p={result.exact_match} exact />
              </div>
            </div>
          )}

          <div>
            <div className="overline mb-2">Closest matches</div>
            {result.closest_matches?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {result.closest_matches.map((p) => (
                  <ResultCard key={p.id} p={p} />
                ))}
              </div>
            ) : (
              <div className="paper-card p-6 text-sm text-stone-500 text-center">
                No products in the catalog match this filter. Add sarees or widen the
                budget / availability.
              </div>
            )}
          </div>
        </div>
      )}

      <div data-testid={MATCH.history} className="paper-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Search history</div>
            <h3 className="font-serif text-xl text-stone-800 mt-1">
              Every past hunt
            </h3>
          </div>
          <div className="text-xs text-stone-500">{history.length} searches</div>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-stone-400 py-6 text-center">
            No searches yet.
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {history.map((h) => (
              <div
                key={h.id}
                data-testid={MATCH.historyRow(h.id)}
                className="py-3 flex items-center gap-3"
              >
                {h.query_image_url ? (
                  <img
                    src={resolveImageUrl(h.query_image_url)}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover border border-stone-200"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-stone-100 grid place-items-center text-stone-400">
                    <Sparkles className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800 truncate">
                    {h.top_match_name || "No match"}
                    {h.top_match_similarity != null && (
                      <span className="text-stone-400 ml-1">
                        · {Math.round(h.top_match_similarity)}%
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500 flex items-center gap-2">
                    <span>{new Date(h.created_at).toLocaleString()}</span>
                    {h.from_manychat && (
                      <span className="heritage-badge bg-terracotta/5 text-terracotta border-terracotta/20">
                        via ManyChat
                      </span>
                    )}
                    {h.sent_to_customer && (
                      <span className="heritage-badge bg-emerald-50 text-emerald-700 border-emerald-200">
                        Sent
                      </span>
                    )}
                  </div>
                </div>
                {!h.sent_to_customer && h.top_match_id && (
                  <button
                    data-testid={MATCH.historyMarkSent(h.id)}
                    onClick={() => markSent(h.id)}
                    className="text-xs text-terracotta hover:underline flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Mark sent
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
